import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { CertificateStatusEnum, ItemTypeEnum, IUserCertificate } from '@share/common-types';
import { Routes } from '@share/constants';
import { getUserCertificatesAsync } from '@share/api';
import {
  filterUserCertificatesByTypeAndStatus,
  FunctionalRootState,
  isPublicClubSiteAnonymousVisitor,
  isPublicClubSiteEnabled,
  resolveCertificateClubSiteId,
} from '@share/utils';

const PUBLIC_CLUBSITE_SECTION_GUARD_CERTIFICATE_STATUSES: CertificateStatusEnum[] = [
  CertificateStatusEnum.Active,
  CertificateStatusEnum.PartiallyRedeemed,
];

interface PublicClubSiteSectionGuardProps {
  children: ReactNode;
  itemType: ItemTypeEnum;
}

/**
 * Gates hotel/condo sections on public ClubSite accounts.
 *
 * The certificate request is owned locally instead of reading `certificatesStore`: the gated
 * children also fetch certificates into that store, so sharing its `loading` flag made the
 * spinner unmount the children, which refetched, which raised the flag again.
 */
export function PublicClubSiteSectionGuard({
  children,
  itemType,
}: PublicClubSiteSectionGuardProps): React.ReactElement {
  const intl = useIntl();
  const history = useHistory();
  const { accountUsername } = useParams<{ accountUsername?: string }>();
  const { account, user, userWallet } = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuOrganizationId = useSelector(
    (state: FunctionalRootState) => state.navigationMenuStore.items?.organizationId,
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [certificates, setCertificates] = useState<IUserCertificate[]>([]);
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  const requestedKeyRef = useRef<string | null>(null);

  const isEnabled = isPublicClubSiteEnabled(account);
  const isAnonymousVisitor = isPublicClubSiteAnonymousVisitor(account);
  const memberUser = userWallet ?? user;
  const memberEmail = memberUser?.email;

  const organizationId = resolveCertificateClubSiteId({
    account,
    user: memberUser,
    menuOrganizationId,
  });

  const requestKey = memberEmail ? `${memberEmail}|${organizationId ?? ''}` : null;

  const catalogPath = accountUsername
    ? `/${accountUsername}${Routes.CertificatesCatalog}`
    : Routes.CertificatesCatalog;

  const isChecking = !requestKey || checkedKey !== requestKey;

  const hasValidCertificate = useMemo(
    () => filterUserCertificatesByTypeAndStatus(certificates, itemType).length > 0,
    [certificates, itemType],
  );

  useEffect(() => {
    if (!isEnabled || isAnonymousVisitor || !requestKey || !memberEmail) {
      return;
    }

    if (requestedKeyRef.current === requestKey) {
      return;
    }

    requestedKeyRef.current = requestKey;
    let isActive = true;

    getUserCertificatesAsync({
      email: memberEmail,
      organizationId,
      statuses: PUBLIC_CLUBSITE_SECTION_GUARD_CERTIFICATE_STATUSES,
      paging: { pageNumber: 1, pageSize: 100 },
    })
      .then(({ data }) => {
        if (!isActive) {
          return;
        }

        setCertificates(data?.items ?? []);
        setCheckedKey(requestKey);
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }

        console.error('Failed to load certificates for public ClubSite section guard', error);
        setCertificates([]);
        setCheckedKey(requestKey);
      });

    return () => {
      isActive = false;
    };
  }, [isAnonymousVisitor, isEnabled, memberEmail, organizationId, requestKey]);

  useEffect(() => {
    if (!isEnabled || isAnonymousVisitor || isChecking || hasValidCertificate) {
      return;
    }

    setIsModalVisible(true);
  }, [hasValidCertificate, isAnonymousVisitor, isChecking, isEnabled]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  if (isAnonymousVisitor) {
    return <Redirect to={catalogPath} />;
  }

  if (isChecking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!hasValidCertificate) {
    return (
      <Modal
        open={isModalVisible}
        title={intl.formatMessage({ id: 'publicClubSite.noCertificate.title' })}
        okText={intl.formatMessage({ id: 'publicClubSite.noCertificate.confirm' })}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => {
          setIsModalVisible(false);
          history.replace(catalogPath);
        }}
        onCancel={() => {
          setIsModalVisible(false);
          history.replace(catalogPath);
        }}
      >
        <FormattedMessage id="publicClubSite.noCertificate.message" />
      </Modal>
    );
  }

  return <>{children}</>;
}
